import { Beaker, ExternalLink, Mail, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAcademicLabs } from "@/hooks/useAcademicMarket";

interface AccountLabsTabProps {
  accountId: string;
}

export function AccountLabsTab({ accountId }: AccountLabsTabProps) {
  const { data: labs, isLoading } = useAcademicLabs(accountId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (!labs || labs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Beaker className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No labs discovered yet</p>
        <p className="text-sm mt-1">Labs are synced from the Life Science Hub</p>
      </div>
    );
  }

  return (
    <div className="divide-y">
      <div className="px-6 py-3 bg-muted/30 text-sm text-muted-foreground">
        {labs.length} research lab{labs.length !== 1 ? "s" : ""}
      </div>
      {labs.map((lab) => (
        <div key={lab.lab_id} className="flex items-start gap-4 px-6 py-4 hover:bg-accent/50 transition-colors">
          <Beaker className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              {lab.lab_website ? (
                <a
                  href={lab.lab_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-sm text-primary hover:underline"
                >
                  {lab.lab_name}
                </a>
              ) : (
                <h3 className="font-medium text-sm">{lab.lab_name}</h3>
              )}
              {lab.principal_investigator && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {lab.principal_investigator}
                </span>
              )}
              {lab.department && (
                <Badge variant="outline" className="text-xs">{lab.department}</Badge>
              )}
            </div>

            {lab.research_focus && (
              <p className="text-sm text-muted-foreground">{lab.research_focus}</p>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              {lab.lab_website && (
                <a
                  href={lab.lab_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {lab.lab_website.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 50)}
                </a>
              )}
              {lab.contact_email && (
                <a
                  href={`mailto:${lab.contact_email}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {lab.contact_email}
                </a>
              )}
            </div>

            {((lab.research_areas && lab.research_areas.length > 0) || (lab.therapeutic_areas && lab.therapeutic_areas.length > 0)) && (
              <div className="flex flex-wrap gap-1 pt-1">
                {(lab.research_areas || []).slice(0, 3).map((area, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {area}
                  </Badge>
                ))}
                {(lab.therapeutic_areas || []).slice(0, 2).map((area, i) => (
                  <Badge key={`t-${i}`} variant="secondary" className="text-xs">
                    {area}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
